import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Trophy } from 'lucide-react';
import { useState } from 'react';

interface BracketMatch {
  id: number;
  player1: string;
  player2: string;
  date: string;
  time: string;
  court: string;
  result?: string;
  sets?: {
    player1: number[];
    player2: number[];
  };
}

interface BracketData {
  octavos: BracketMatch[];
  cuartos: BracketMatch[];
  semifinales: BracketMatch[];
  final: BracketMatch[];
}

interface CollapsibleBracketsProps {
  bracketData: BracketData;
  gameType: 'singles' | 'doubles';
  category?: string;
  subcategory?: string;
  tournamentRules?: any;
}

const CollapsibleBrackets = ({ bracketData, gameType, category, subcategory, tournamentRules }: CollapsibleBracketsProps) => {
  const [openRounds, setOpenRounds] = useState<Record<string, boolean>>({
    octavos: false,
    cuartos: false,
    semifinales: false,
    final: true // Final always open by default
  });

  const toggleRound = (round: string) => {
    setOpenRounds(prev => ({
      ...prev,
      [round]: !prev[round]
    }));
  };

  const formatPlayerName = (playerName: string) => {
    if (gameType === 'singles' && playerName.includes('/')) {
      return playerName.split('/')[0];
    }
    return playerName;
  };

  const getRoundGradient = (roundKey: string) => {
    return 'bg-[#e7d1b6] border-[#d4b896]';
  };

  const renderCompactMatch = (match: BracketMatch, roundKey: string) => (
    <div key={match.id} className={`rounded-lg p-4 shadow-md transition-all hover:shadow-lg hover:scale-[1.02] animate-fade-in ${getRoundGradient(roundKey)}`}>
      <div className="space-y-3">
        {/* Players */}
        <div className="space-y-2">
          <div className="flex items-center">
            <span className="text-sm font-bold text-gray-900">
              {formatPlayerName(match.player1)}
            </span>
          </div>
          
          <div className="flex items-center">
            <span className="text-sm font-bold text-gray-900">
              {formatPlayerName(match.player2)}
            </span>
          </div>
        </div>
        
        {/* Match info */}
        <div className="flex items-center justify-between pt-3 border-t-2 border-gray-200">
          <div className="flex flex-col space-y-1 text-xs text-gray-700 font-medium">
            <span>📅 {match.date}</span>
            <span>🕐 {match.time}</span>
            <span>🎾 Cancha {match.court}</span>
          </div>
          
          {match.result && (
            <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs shadow-sm">
              ✓ Finalizado
            </Badge>
          )}
        </div>
      </div>
    </div>
  );

  // Filter out empty rounds to only show phases with matches
  const rounds = [
    { key: 'octavos', title: 'Octavos de Final', matches: bracketData.octavos || [] },
    { key: 'cuartos', title: 'Cuartos de Final', matches: bracketData.cuartos || [] },
    { key: 'semifinales', title: 'Semifinales', matches: bracketData.semifinales || [] },
    { key: 'final', title: 'Final', matches: bracketData.final || [] }
  ].filter(round => round.matches.length > 0);

  const getRoundIcon = (roundKey: string) => {
    switch (roundKey) {
      case 'final':
        return '🏆';
      case 'semifinales':
        return '🥈';
      case 'cuartos':
        return '🥉';
      case 'octavos':
        return '🎯';
      default:
        return '⚡';
    }
  };

  const getRoundBadgeColor = (roundKey: string) => {
    switch (roundKey) {
      case 'final':
        return 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white';
      case 'semifinales':
        return 'bg-gradient-to-r from-gray-400 to-slate-500 text-white';
      case 'cuartos':
        return 'bg-gradient-to-r from-orange-400 to-amber-500 text-white';
      case 'octavos':
        return 'bg-gradient-to-r from-primary/60 to-primary text-primary-foreground';
      default:
        return 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-800';
    }
  };

  const hasMatchesWithPlayers = () => {
    return rounds.some(round => 
      round.matches.some(match => 
        match.player1 && match.player1 !== 'Por definir' && 
        match.player2 && match.player2 !== 'Por definir'
      )
    );
  };

  const getClassificationText = () => {
    if (!tournamentRules || !category || !subcategory) {
      return "Clasificación por definir";
    }
    
    try {
      const categoryRules = tournamentRules.genderCategories?.[category]?.find(
        (cat: any) => cat.category === subcategory
      );
      
      if (!categoryRules) return "Clasificación por definir";
      
      const groupsMin = categoryRules.groupsMin || 0;
      const wildCardCount = categoryRules.wildCardCount || 0;
      const total = groupsMin + wildCardCount;
      
      if (wildCardCount > 0) {
        return `${groupsMin} primeros lugares + ${wildCardCount} mejores 2dos = ${total} equipos`;
      }
      return `${groupsMin} primeros lugares = ${total} equipos`;
    } catch (error) {
      console.error('[CollapsibleBrackets] Error calculating classification:', error);
      return "Clasificación por definir";
    }
  };

  const getPhaseStructure = () => {
    if (!tournamentRules || !category || !subcategory) return "";
    
    try {
      const categoryRules = tournamentRules.genderCategories?.[category]?.find(
        (cat: any) => cat.category === subcategory
      );
      
      if (!categoryRules) return "";
      
      const nextPhase = categoryRules.nextPhase;
      
      // Determinar la secuencia de fases basada en nextPhase
      const phases = [];
      if (nextPhase === 'octavos') {
        phases.push('Octavos de Final', 'Cuartos de Final', 'Semifinales', 'Final');
      } else if (nextPhase === 'cuartos') {
        phases.push('Cuartos de Final', 'Semifinales', 'Final');
      } else if (nextPhase === 'semifinales') {
        phases.push('Semifinales', 'Final');
      } else {
        phases.push('Final');
      }
      
      return phases.length > 1 ? `Estructura: ${phases.join(' → ')}` : '';
    } catch (error) {
      console.error('[CollapsibleBrackets] Error calculating phase structure:', error);
      return "";
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader className="bg-gradient-to-r from-[#1b3521] via-[#31633f] to-[#6da079] text-white">
        <CardTitle className="flex items-center gap-2 text-base font-bold">
          <Trophy className="w-5 h-5" />
          <span>Fase de Eliminación</span>
        </CardTitle>
        <div className="bg-white/20 p-3 rounded-lg border border-white/30 mt-3">
          <p className="text-sm text-white font-medium">
            <strong>Clasifican:</strong> {getClassificationText()}
          </p>
          {getPhaseStructure() && (
            <p className="text-xs text-white/80 mt-1">
              {getPhaseStructure()}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {rounds.map((round) => (
          <Collapsible 
            key={round.key}
            open={openRounds[round.key]}
            onOpenChange={() => toggleRound(round.key)}
          >
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between p-4 h-auto hover:bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">{getRoundIcon(round.key)}</span>
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <span className="font-bold text-gray-900 text-sm">{round.title}</span>
                    <Badge className={`text-xs shadow-sm self-start ${getRoundBadgeColor(round.key)}`}>
                      {round.matches.length} {round.matches.length === 1 ? 'partido' : 'partidos'}
                    </Badge>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-2">
                  {openRounds[round.key] ? (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  )}
                </div>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 mt-3 animate-accordion-down">
              {round.matches.map((match) => renderCompactMatch(match, round.key))}
              
              {/* Champion indicator for final */}
              {round.key === 'final' && (
                <div className="text-center pt-6 pb-2 animate-fade-in">
                  <div className="relative inline-block">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 rounded-full flex items-center justify-center shadow-2xl mx-auto animate-pulse">
                      <span className="text-3xl">🏆</span>
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-300 rounded-full animate-ping" />
                  </div>
                  <p className="text-base font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-amber-600 mt-3">
                    ⭐ CAMPEÓN ⭐
                  </p>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
};

export default CollapsibleBrackets;